'use client';

import { useEffect, useRef, useState, useMemo } from 'react';

import { Types } from '@scorenco/core';
import { v4 as uuidv4 } from 'uuid';

import { TextAd } from './TextAds';

interface UseTextAdParams {
  display_type?: string;
  sport_id?: Types.Clubs_Sport['id'];
  department_id?: Types.Places_Department['id'];
  isLoggedIn?: number;
  screen?: string;
  widgetId?: number;
}

// Cache module-level pour éviter les re-fetch entre renders/remounts
const instancesCache = new Map<
  string,
  { uuid: string; text: TextAd | null; shouldShow: boolean }
>();

const buildInstanceKey = ({
  display_type,
  sport_id,
  department_id,
  widgetId,
}: UseTextAdParams) =>
  [
    display_type,
    sport_id ?? 'no-sport',
    department_id ?? 'no-dept',
    widgetId ? `widget-${widgetId}` : 'no-widget',
  ].join('-');

const extractTextAd = (
  data: Record<string, unknown>,
  display_type: string,
  uuid: string
): TextAd | null => {
  const raw = data.text ?? (data.content ? data : data.message ? data : null);
  if (!raw) return null;

  // Normaliser : si la source est `data.message`, mapper vers `content`
  const source = (data.text ?? data) as Record<string, unknown>;
  const ad: TextAd = {
    id: (source.id as string) || uuid,
    content: (source.content as string) || (source.message as string) || '',
    icon: (source.icon as string) || '',
    display_type: (source.display_type as string) || display_type,
    campaign_url: (source.campaign_url as string) || '',
    position: (source.position as string) || '1',
    sport_id: source.sport_id as string | undefined,
    department_id: source.department_id as string | undefined,
    impression_pixel_url: source.impression_pixel_url as string | undefined,
    link_url: source.link_url as string | undefined,
    logo_url: source.logo_url as string | undefined,
  };

  // Gérer le cas où content est un objet imbriqué
  if (typeof ad.content === 'object' && ad.content !== null) {
    const nested = ad.content as unknown as Record<string, unknown>;
    return {
      ...ad,
      id: (nested.id as string) || ad.id,
      content: (nested.content as string) || (nested.message as string) || '',
      icon: (nested.icon as string) || ad.icon,
      display_type: (nested.display_type as string) || ad.display_type,
      campaign_url: (nested.campaign_url as string) || ad.campaign_url,
      position: (nested.position as string) || ad.position,
      sport_id: nested.sport_id as string | undefined,
      department_id: nested.department_id as string | undefined,
      impression_pixel_url: nested.impression_pixel_url as string | undefined,
      link_url: nested.link_url as string | undefined,
      logo_url: (nested.logo_url as string) || ad.logo_url,
    };
  }

  return ad;
};

const hasVisibleContent = (ad: TextAd): boolean =>
  (typeof ad.content === 'string' && ad.content.trim().length > 0) ||
  !!ad.logo_url?.trim() ||
  !!ad.icon?.toString().trim();

export const useTextAd = (params: UseTextAdParams) => {
  const {
    display_type,
    sport_id,
    department_id,
    isLoggedIn,
    screen,
    widgetId,
  } = params;

  const [text, setText] = useState<TextAd | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const hasBeenHiddenRef = useRef(false);
  const [hasBeenHidden, setHasBeenHidden] = useState(false);

  const instanceKey = buildInstanceKey(params);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        hasBeenHiddenRef.current = true;
        setHasBeenHidden(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    const instance = instancesCache.get(instanceKey) ?? {
      uuid: uuidv4(),
      text: null,
      shouldShow: true,
    };
    instancesCache.set(instanceKey, instance);

    const cacheAndSet = (ad: TextAd | null) => {
      instance.text = ad;
      setText(ad);
      instancesCache.set(instanceKey, instance);
    };

    const controller = new AbortController();

    const fetchTextAd = async () => {
      try {
        setIsLoading(true);
        setError(false);

        const searchParams = new URLSearchParams({
          display_type: display_type || '',
          logged_in: (isLoggedIn ?? 0).toString(),
          screen: display_type === 'widget' ? '' : screen || 'desktop',
          position: '1',
          format: 'json',
          id: instance.uuid,
        });
        if (sport_id) searchParams.append('sport', sport_id.toString());
        if (department_id)
          searchParams.append('department', department_id.toString());
        if (widgetId) searchParams.append('widget_id', widgetId.toString());

        const basePath = widgetId ? 'widgets/ad' : 'ads/text';
        const response = await fetch(
          `https://${process.env.NEXT_PUBLIC_CACHE_ENDPOINT}/${basePath}/?${searchParams}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let data: Record<string, unknown>;
        try {
          data = await response.json();
        } catch {
          cacheAndSet(null);
          return;
        }

        cacheAndSet(extractTextAd(data, display_type || '', instance.uuid));
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Erreur lors de la récupération de la pub texte:', err);
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void (async () => {
      if (instance.text) {
        setText(instance.text);
        setIsLoading(false);
        return;
      }

      if (!instance.shouldShow || hasBeenHiddenRef.current) return;

      await fetchTextAd();
    })();

    return () => controller.abort();
  }, [instanceKey, display_type, sport_id, department_id, isLoggedIn, screen]);

  const isAvailable = useMemo(
    () =>
      !!(
        text &&
        !hasBeenHidden &&
        !isLoading &&
        !error &&
        hasVisibleContent(text)
      ),
    [text, isLoading, error, hasBeenHidden]
  );

  return { text, isLoading, error, isAvailable };
};
